import React, { useState, useEffect, useRef } from 'react';
import "../map/CrimeMap.css";
import CrimeFilters from './CrimeFilters';
import TimeFilters from './DateFilters';
import LocationFilters from './LocationFilters';
import WhereFilters from './WhereFilters';
import SummaryInfo from './SummaryInfo';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faQuestionCircle,
    faCalendarAlt,
    faSearchLocation,
    faMapMarkerAlt,
    faInfo,
    faCommentDots,
    faUser,
    faBook
} from '@fortawesome/free-solid-svg-icons'
import LogIn from './LogIn';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'absolute',
        height: '100vh',
        float: 'left',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    helpSection: {
        borderTop: '1px solid rgba(0,0,0,0.12)',
        paddingBottom: '4px',
    },
    helpDialogSection: {
        marginTop: '16px',
    },
    tab: {
        minWidth: '0px',
    },
    filterLabel: {
        margin: '6px 0px',
        display: 'flex',
        justifyContent: 'center'
    },
    tooltip: {
        fontSize: '0.85rem'
    }
}));

export const Sidebar = () => {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const handleChange = (event, newValue) => setActiveTab(newValue);
    const closeDrawer = () => setActiveTab(false);

    const sidebarRef = useRef(null);
    const [sidebarWidth, setSidebarWidth] = useState();

    const Legend = () => {
        return(
            <div>
                <Divider variant="middle"/><br/>
                <h3 className={classes.filterLabel}>Legend</h3>
                <Divider variant="middle"/>
                <Tabs
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label="filtering categories"
                >
                    <Tab classes={{root: classes.tab}}
                         icon={<img src='https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png' alt="Blue Pin" style={{width: "20px"}}/>}
                         iconPosition="start"
                         label="Incidents"
                         sx={{
                             justifyContent: "flex-start", // Aligns content to the left
                             textTransform: "none", // Keeps label text case as is
                         }}
                    />
                    <Tooltip title="Represents a Dispatch Event where an officer may or may not have responded." placement="right" arrow classes={{tooltip: classes.tooltip}}>
                        <Tab classes={{root: classes.tab}}
                             icon={<img src='https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png' alt="Green Pin" style={{width: "20px"}}/>}
                             iconPosition="start"
                             label="Calls"/>
                    </Tooltip>
                    <Tab classes={{root: classes.tab}}
                         icon={<img src='https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png' alt="Red Pin" style={{width: "20px"}}/>}
                         iconPosition="start"
                         label="Search"/>
                </Tabs>
            </div>
        )
    }

    useEffect(() => {
        if(sidebarRef.current){
            setSidebarWidth(sidebarRef.current.offsetWidth);
        }
    }, [sidebarRef]);

    return (
        <div>
            <Paper square elevation={2} className={classes.root} ref={sidebarRef}>
                <div>
                    <h3 className={classes.filterLabel}>Filters</h3>
                    <Divider variant="middle"/>
                    <Tabs
                        orientation="vertical"
                        value={activeTab}
                        onChange={handleChange}
                        indicatorColor="primary"
                        textColor="primary"
                        aria-label="filtering categories"
                    >
                        <Tab classes={{root: classes.tab}} icon={<FontAwesomeIcon icon={faQuestionCircle} size='2x'/>}
                             label="What"/>
                        <Tab classes={{root: classes.tab}} icon={<FontAwesomeIcon icon={faCalendarAlt} size='2x'/>}
                             label="When"/>
                        <Tab classes={{root: classes.tab}} icon={<FontAwesomeIcon icon={faMapMarkerAlt} size='2x'/>}
                             label="Where"/>
                        {/*
                        COMMENTING OUT THE ORIGINAL LOCATIONFILTERS WHERE TAB
                        <Tab classes={{ root: classes.tab }} icon={<FontAwesomeIcon icon={faSearchLocation} size='2x'/>} label="Where" />
                        */}
                        <Tab classes={{root: classes.tab}} icon={<FontAwesomeIcon icon={faInfo} size='2x'/>}
                             label="Summary"/>
                        {/*
                        COMMENTING OUT LOGIN
                        <Tab classes={{ root: classes.tab }} icon={<FontAwesomeIcon icon={faUser} size='2x'/>} label="Login" />
                        */}
                    </Tabs>

                    <Legend/>
                </div>

                <div className={classes.helpSection}>
                    <Tabs orientation="vertical">
                        <Tab
                            classes={{root: classes.tab}}
                            icon={<FontAwesomeIcon icon={faBook} size='2x'/>}
                            label="Help"
                            onClick={() => setHelpOpen(true)}
                        />
                    </Tabs>
                </div>
            </Paper>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>How to Use the Crime Map</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="h6" gutterBottom>Filters – Left Sidebar</Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>WHAT:</strong> Select the "What" icon to filter the map events by crime type, crime details or call type. Use this tab to select whether incidents and/or calls are displayed.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>WHEN:</strong> Select the "When" icon to change the date range for displayed events.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>WHERE:</strong> Select the "Where" icon to search for events within a radius of a specific address. Enter the address and set the radius slider as needed.
                    </Typography>

                    <Typography variant="h6" gutterBottom className={classes.helpDialogSection}>Additional Actions – Right Tool Bar</Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>ZOOM:</strong> To zoom in or out on the map, select the + or – icon.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>SHAPES:</strong> Select the polygon, square or circle icon. Then click on the map to add the shape and size the shape. The map will filter events to within that shape.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>EDIT:</strong> After adding a shape to the map, select the edit icon and select the shape on the map to resize by expanding or shrinking the shape.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>DELETE:</strong> Select the delete icon to delete any shape added to the map. This will set the events back to the original filter criteria.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>HEAT MAP:</strong> Select the icons in the lower right tool bar to toggle incidents and/or calls between cluster and heat map.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)} color="primary" variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
            <CrimeFilters sidebarWidth={sidebarWidth} selected={activeTab === 0} close={closeDrawer}/>
            <TimeFilters sidebarWidth={sidebarWidth} selected={activeTab === 1} close={closeDrawer}/>
            <WhereFilters sidebarWidth={sidebarWidth} selected={activeTab === 2} close={closeDrawer}/>
            {/*
            COMMENTING OUT THE ORIGINAL LOCATIONFILTERS WHERE TAB
            <LocationFilters sidebarWidth={sidebarWidth} selected={activeTab===2} close={closeDrawer}/>
            */}
            <SummaryInfo sidebarWidth={sidebarWidth} selected={activeTab === 3} close={closeDrawer}/>
            {/*
            COMMENTING OUT LOGIN
            <LogIn sidebarWidth={sidebarWidth} selected={activeTab===4} close={closeDrawer}/>
            */}
        </div>
    );
}

